import { Component } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { EntityState } from "breeze-client";
import _ from "lodash";
import { ActiveStatusEnum, FeatureChoice, Product, ProductFeatureChoice } from "app/model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "./dialog-base.component";
import { DomainService } from "./domain.service";
import { StagedProductColorDTO } from "@/admin/staged-feed.models";
import { ProductExposureApiService, ProductExposureFns } from "./product-exposure-api.service";

export interface RepointColorDialogParams extends DialogParams {
  /**
   * Every not-inactive ProductType for this manufacturer style. A style can legitimately
   * span several, and the color being changed may sit on any of them - so the dialog works
   * over the same set the grid counted, or the two would disagree.
   */
  productTypeIds: number[];
  /** Named styleCode, not style: DialogBaseComponent spreads params into PrimeNG's
   *  DynamicDialogConfig, where `style` is the dialog's own CSS style object. */
  styleCode: string;
  /** The Uniformax color being moved off. */
  fromFeatureChoiceId: number;
  fromColorName: string;
  /** Everything the manufacturer publishes for this style. */
  stagedColors: StagedProductColorDTO[];
}

interface TargetOption {
  label: string;
  featureChoice: FeatureChoice;
  pms: string | null;
  alreadyOnStyle: boolean;
}

/**
 * Moves every product of one style from one color to another.
 *
 * Runs through Breeze so the save interceptor writes the ProxSubmission rows and the change
 * reaches Proximity on the next push. The old map row is deleted and a new one added rather
 * than updated, because the pair IS the primary key.
 */
@Component({
  templateUrl: "./repoint-color-dialog.component.html",
  standalone: false,
})
export class RepointColorDialogComponent extends DialogBaseComponent<RepointColorDialogParams> {
  targets: TargetOption[] = [];
  selected: TargetOption | null = null;

  products: Product[] = [];
  affectedCount = 0;
  isSaving = false;
  errorMessage = "";

  constructor(
    public config: DynamicDialogConfig<RepointColorDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private productExposureApi: ProductExposureApiService
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, { header: "Change Color", width: "760px", height: "840px" });
    this.prepare();
  }

  private async prepare() {
    const lists = await Promise.all(
      this.params.productTypeIds.map(id => this.dbQueryService.getProductsForProductType(id)));
    // Retired products are not sold, so they are neither a finding nor something to change.
    this.products = _.flatten(lists).filter(p => p.activeStatusId !== ActiveStatusEnum.Inactive);
    this.affectedCount = this.products.filter(p => this.usesFrom(p)).length;

    // Resolve each published color name against the live list of the from-choice's OWN feature,
    // rather than trusting a stored match: a color added since the feed was read must still be
    // selectable here. Never "the color feature": prod has two Color-kind features (58 Color,
    // 59 Men's trousers), and the first one by name offered Color choices as the target for a
    // trouser size, which put a color in a size slot on every product (feed audit E2).
    const fromFeatureId = (await this.dbQueryService.getFeatureChoice(this.params.fromFeatureChoiceId))?.featureId;
    const allChoices = fromFeatureId == null ? [] : await this.dbQueryService.getFeatureChoices(fromFeatureId);
    const byName = new Map<string, FeatureChoice>();
    allChoices
      .filter(fc => fc.activeStatusId !== ActiveStatusEnum.Inactive)
      .forEach(fc => byName.set((fc.choiceValue ?? "").trim().toLowerCase(), fc));

    const onStyle = new Set<number>();
    this.products.forEach(p =>
      p.productFeatureChoices
        .filter(pfc => pfc.featureChoice?.featureId === fromFeatureId)
        .forEach(pfc => onStyle.add(pfc.featureChoiceId))
    );

    const seen = new Set<number>();
    this.params.stagedColors.forEach(sc => {
      // Try BOTH published names, the same way the sweep does when it stamps
      // StagedProductColor.FeatureChoiceId. Preferring standardColorName and stopping there
      // made this dialog stricter than the stored match, so a color the feed page showed as
      // matched could not be selected here: SanMar publishes J763 as standard 'Navy/ Black'
      // and catalogue 'Navy/Black', and only the second one is our color.
      const fc = StagedColorFns.resolve(byName, sc);
      if (!fc || fc.id === this.params.fromFeatureChoiceId || seen.has(fc.id)) return;
      seen.add(fc.id);
      this.targets.push({
        label: sc.StandardColorName || sc.ColorName,
        featureChoice: fc,
        pms: sc.ApproximatePms,
        alreadyOnStyle: onStyle.has(fc.id),
      });
    });
    this.targets = _.sortBy(this.targets, t => t.label);
    this.isPageReady = true;
  }

  private usesFrom(p: Product) {
    return p.productFeatureChoices.some(pfc => pfc.featureChoiceId === this.params.fromFeatureChoiceId);
  }

  get canSave() {
    return this.selected != null && !this.selected.alreadyOnStyle && this.affectedCount > 0 && !this.isSaving;
  }

  async onOk() {
    if (!this.canSave || this.selected == null) return;
    const target = this.selected;
    this.isSaving = true;
    this.errorMessage = "";

    const use = await this.productExposureApi
      .getForProducts(this.products.filter(p => this.usesFrom(p)).map(p => p.id))
      .catch(() => null);
    const ok = await this.dialogService.yesNoDialog(
      "Change Color",
      `The system changes ${this.affectedCount} product(s) on style ${this.params.styleCode}`
        + ` from '${this.params.fromColorName}' to '${target.label}'.`
        + ProductExposureFns.reassignColorWarning(use)
        + `<br/><br/>The change is sent to Proximity on the next push. Proximity price lists and`
        + ` program product lists that refer to this color are affected.`
        + `<br/><br/>Do you want to continue?`,
      "Ok",
      "Cancel"
    );
    if (!ok) {
      this.isSaving = false;
      return;
    }

    try {
      this.products.filter(p => this.usesFrom(p)).forEach(p => {
        const old = p.productFeatureChoices.find(x => x.featureChoiceId === this.params.fromFeatureChoiceId);
        if (old == null) return;
        // Delete then add. The pair is the primary key, so an update is not available.
        old.entityAspect.setDeleted();
        this.dbSaveService.createEntity(ProductFeatureChoice, {
          productId: p.id,
          featureChoiceId: target.featureChoice.id,
        });
      });

      const saved = await this.dbSaveService.saveChanges();
      if (!saved) {
        this.isSaving = false;
        return;
      }
      this.dialogRef.close({ changed: this.affectedCount, toName: target.label });
    } catch (e: any) {
      this.uow.rollback();
      this.errorMessage = `The system could not change the color. ${e?.message ?? ""}`;
      this.isSaving = false;
    }
  }

  onCancel() {
    this.uow.rollback();
    this.dialogRef.close(null);
  }
}


/**
 * Resolving a published color to a Uniformax color.
 *
 * A manufacturer publishes two names per color and they are not always the same string.
 * SanMar sends J763 as standardColorName 'Navy/ Black' and catalogueColorName 'Navy/Black';
 * only the second matches our color. StagedFeedService.ResolveFeatureChoicesAsync has always
 * tried both when it stamps StagedProductColor.FeatureChoiceId, so anything that resolves by
 * name afterwards has to try both as well or it ends up stricter than the stored match -
 * showing a color as matched on the grid and then refusing to offer it.
 */
export class StagedColorFns {
  static resolve(byName: Map<string, FeatureChoice>, sc: StagedProductColorDTO): FeatureChoice | null {
    return byName.get(StagedColorFns.key(sc.StandardColorName))
        ?? byName.get(StagedColorFns.key(sc.ColorName))
        ?? null;
  }

  static key(name: string | null | undefined) {
    return (name ?? "").trim().toLowerCase();
  }
}
